import SysUser from "app/class/sysUser";

export const DEFAULT_TIME_ZONE = "UTC";
export const DEFAULT_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";
export const DEFAULT_TIME_FORMAT = "HH:mm";

interface DateTimeParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

export interface DateTimePreferences {
    timeZone: string;
    dateTimeFormat: string;
    dateFormat: string;
    timeFormat: string;
}

const pad = (value: number, size = 2) => String(value).padStart(size, "0");

export const normalizeTimeZone = (timeZone?: string | null) => {
    const candidate = timeZone || DEFAULT_TIME_ZONE;

    try {
        new Intl.DateTimeFormat("en-US", { timeZone: candidate }).format(new Date());
        return candidate;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
};

export const getDateFormatFromDateTimeFormat = (format?: string | null) => {
    const candidate = format || DEFAULT_DATE_TIME_FORMAT;
    const dateToken = candidate.split(/\s+/).find((part) => /Y|M|D/.test(part));

    return dateToken || DEFAULT_DATE_FORMAT;
};

export const getTimeFormatFromDateTimeFormat = (format?: string | null) => {
    const candidate = format || DEFAULT_DATE_TIME_FORMAT;
    const timeToken = candidate.split(/\s+/).find((part) => /H|h|m|s|A|a/.test(part));

    return timeToken || DEFAULT_TIME_FORMAT;
};

export const getUserDateTimePreferences = (user?: SysUser | null): DateTimePreferences => {
    const dateTimeFormat = user?.dateTimeFormat || DEFAULT_DATE_TIME_FORMAT;

    return {
        timeZone: normalizeTimeZone(user?.timeZone),
        dateTimeFormat,
        dateFormat: getDateFormatFromDateTimeFormat(dateTimeFormat),
        timeFormat: getTimeFormatFromDateTimeFormat(dateTimeFormat),
    };
};

export const toDateFnsPickerFormat = (format: string) => {
    const tokens: Record<string, string> = {
        YYYY: "yyyy",
        YY: "yy",
        MM: "MM",
        M: "M",
        DD: "dd",
        D: "d",
        HH: "HH",
        H: "H",
        hh: "hh",
        h: "h",
        mm: "mm",
        m: "m",
        ss: "ss",
        s: "s",
        A: "a",
        a: "a",
    };

    return format.replace(/YYYY|YY|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g, (token) => tokens[token]);
};

const getZonedParts = (date: Date, timeZone: string): DateTimeParts => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).formatToParts(date);

    const valueFor = (type: Intl.DateTimeFormatPartTypes) =>
        Number(parts.find((part) => part.type === type)?.value || 0);
    const rawHour = valueFor("hour");

    return {
        year: valueFor("year"),
        month: valueFor("month"),
        day: valueFor("day"),
        hour: rawHour === 24 ? 0 : rawHour,
        minute: valueFor("minute"),
        second: valueFor("second"),
    };
};

const replaceDateTimeTokens = (format: string, parts: DateTimeParts) => {
    const twelveHour = parts.hour % 12 || 12;
    const meridiem = parts.hour >= 12 ? "PM" : "AM";
    const tokens: Record<string, string> = {
        YYYY: pad(parts.year, 4),
        YY: pad(parts.year % 100),
        MM: pad(parts.month),
        M: String(parts.month),
        DD: pad(parts.day),
        D: String(parts.day),
        HH: pad(parts.hour),
        H: String(parts.hour),
        hh: pad(twelveHour),
        h: String(twelveHour),
        mm: pad(parts.minute),
        m: String(parts.minute),
        ss: pad(parts.second),
        s: String(parts.second),
        A: meridiem,
        a: meridiem.toLowerCase(),
    };

    return format.replace(/YYYY|YY|MM|M|DD|D|HH|H|hh|h|mm|m|ss|s|A|a/g, (token) => tokens[token]);
};

const parseDate = (value: string | null | undefined) => {
    if (!value) return null;
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

export const isValidDateObject = (value: unknown): value is Date =>
    value instanceof Date && !Number.isNaN(value.getTime());

const dateOnlyValueToDate = (value: string | null | undefined) => {
    if (!value) return null;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return parseDate(value);

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const isPortuguese = (language?: string | null) => language?.toLowerCase().startsWith("pt");

export const formatRelativeTimeFromNow = (
    value: string | null | undefined,
    options: { language?: string | null; dateOnly?: boolean } = {}
) => {
    const date = options.dateOnly ? dateOnlyValueToDate(value) : parseDate(value);
    if (!date) return "";

    const diffMs = Date.now() - date.getTime();
    const absSeconds = Math.max(0, Math.round(Math.abs(diffMs) / 1000));
    const pt = isPortuguese(options.language);

    if (absSeconds < 5) {
        return pt ? "agora" : "now";
    }

    const units = [
        { seconds: 365 * 24 * 60 * 60, label: "y" },
        { seconds: 30 * 24 * 60 * 60, label: "mo" },
        { seconds: 7 * 24 * 60 * 60, label: "w" },
        { seconds: 24 * 60 * 60, label: "d" },
        { seconds: 60 * 60, label: "h" },
        { seconds: 60, label: "min" },
        { seconds: 1, label: "s" },
    ];
    const unit = units.find((candidate) => absSeconds >= candidate.seconds) || units[units.length - 1];
    const amount = Math.max(1, Math.round(absSeconds / unit.seconds));
    const compactValue = `${amount}${unit.label}`;

    if (diffMs >= 0) {
        return pt ? `${compactValue} atrás` : `${compactValue} ago`;
    }

    return pt ? `faltam ${compactValue}` : `${compactValue} left`;
};

export const formatDateTimeValue = (
    value: string | null | undefined,
    preferences: DateTimePreferences
) => {
    const date = parseDate(value);
    if (!date) return "";

    return replaceDateTimeTokens(
        preferences.dateTimeFormat,
        getZonedParts(date, preferences.timeZone)
    );
};

export const formatDateValue = (
    value: string | null | undefined,
    preferences: Pick<DateTimePreferences, "dateFormat">
) => {
    if (!value) return "";
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const parts = match
        ? {
            year: Number(match[1]),
            month: Number(match[2]),
            day: Number(match[3]),
            hour: 0,
            minute: 0,
            second: 0,
        }
        : (() => {
            const date = parseDate(value);
            return date
                ? {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                    hour: 0,
                    minute: 0,
                    second: 0,
                }
                : null;
        })();

    return parts ? replaceDateTimeTokens(preferences.dateFormat, parts) : "";
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string) => {
    const parts = getZonedParts(date, timeZone);
    const zonedAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        date.getUTCMilliseconds()
    );

    return zonedAsUtc - date.getTime();
};

export const utcIsoToZonedPickerDate = (value: string | null | undefined, timeZone: string) => {
    const date = parseDate(value);
    if (!date) return null;

    const parts = getZonedParts(date, normalizeTimeZone(timeZone));
    return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, date.getUTCMilliseconds());
};

export const zonedPickerDateToUtcIso = (value: Date | null, timeZone: string) => {
    if (!isValidDateObject(value)) return null;

    const normalizedTimeZone = normalizeTimeZone(timeZone);
    const wallTimeAsUtc = Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
        value.getMilliseconds()
    );
    let utcTime = wallTimeAsUtc - getTimeZoneOffsetMs(new Date(wallTimeAsUtc), normalizedTimeZone);
    utcTime = wallTimeAsUtc - getTimeZoneOffsetMs(new Date(utcTime), normalizedTimeZone);

    return new Date(utcTime).toISOString();
};

export const dateValueToPickerDate = (value: string | null | undefined) => {
    if (!value) return null;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    const date = parseDate(value);
    return date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
};

export const pickerDateToDateValue = (value: Date | null) => {
    if (!isValidDateObject(value)) return null;

    return `${pad(value.getFullYear(), 4)}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

export const timeValueToPickerDate = (value: string | null | undefined) => {
    if (!value) return null;

    const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
    if (!match) return null;

    const date = new Date();
    date.setHours(Number(match[1]), Number(match[2]), 0, 0);
    return date;
};

export const pickerDateToTimeValue = (value: Date | null) => {
    if (!isValidDateObject(value)) return null;

    return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

export const formatTimeValue = (
    value: string | null | undefined,
    preferences: Pick<DateTimePreferences, "timeFormat">
) => {
    const date = timeValueToPickerDate(value);
    if (!date) return "";

    return replaceDateTimeTokens(preferences.timeFormat, {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
    });
};
